import { useMemo } from 'react'
import Box from '@mui/material/Box'
import useTheme from '@mui/material/styles/useTheme'

import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import { useLocation, Link } from 'react-router-dom'
import { RoutesData } from '@/router/RoutesData'
import { PATHS_WITH_SECTIONS } from '@/router/RoutesUtils'
import { getMyNetworkFirstLink } from '@/pages/MyNetwork/utils/constants'
import { useRestrictedUserPermissions } from '@/hooks/permissions/useRestrictedUserPermissions'
import { NavigationTestIds } from '../../../automation/playwright/test-ids'
import MyReferralsIcon from './Icons/MyReferralsIcon'
import MarketplaceIcon from './Icons/MarketplaceIcon'
import NetworkIcon from './Icons/NetworkIcon'
import AnalyticsIcon from './Icons/AnalyticsIcon'

const StyledLink = styled(Link)(
  ({ theme }) => `
  flex-grow: 1;
  ${theme.breakpoints.up('md')} {
    max-width: 200px;
  }
`
)

const StyledButton = styled(Button)(
  ({ theme }) => `
  color: ${theme.palette.text.secondary};

  min-height: 64px;
  border-bottom: 4px solid transparent;
  border-radius: 0;
  background-color: ${theme.palette.common.white};
  width: 100%;
  white-space: nowrap;
  flex-direction: column;
  &:hover {
    background-color: ${theme.palette.primary.lightest};
  }
  &:active,
  &:focus,
  &.active {
    background-color: ${theme.palette.secondary.lightest};
    border-bottom: 4px solid ${theme.palette.secondary.main};
    color: ${theme.palette.primary.main};
  }
  .MuiButton-startIcon {
    svg {
      font-size: 24px;
    }
  }
  ${theme.breakpoints.up('md')} {
    flex-direction: initial;
    .MuiButton-startIcon {
      margin-left: 0;
      margin-right: ${theme.spacing(1)};
    }
  }
  ${theme.breakpoints.down('md')} {
    padding: 0;
    .MuiButton-startIcon {
      width: 100%;
      display: flex;
      justify-content: center;
      margin-inline: 0px;
    }
  }
`
)

const LinksData = [
  {
    label: 'My Cases',
    icon: MyReferralsIcon,
    route: RoutesData.RMyReferrals.path,
    routesBase: [RoutesData.RMyReferrals.path],
    'data-testid': NavigationTestIds.myReferralsLink,
  },
  {
    label: 'Marketplace',
    icon: MarketplaceIcon,
    route: RoutesData.RMarketplace.path,
    routesBase: [
      RoutesData.RMarketplace.path,
      PATHS_WITH_SECTIONS.CASE_WITH_ID,
    ],
    'data-testid': NavigationTestIds.marketplaceLink,
  },
  {
    label: 'My Network',
    icon: NetworkIcon,
    route: getMyNetworkFirstLink(),
    routesBase: [PATHS_WITH_SECTIONS.MY_NETWORK],
    'data-testid': NavigationTestIds.myNetworkLink,
  },
  {
    label: 'Analytics',
    icon: AnalyticsIcon,
    route: RoutesData.RAnalytics.path,
    routesBase: [RoutesData.RAnalytics.path],
    'data-testid': NavigationTestIds.analyticsLink,
  },
]

interface LinksProps {
  /**
   * Ask Attorney Share's circle is in the bar: less padding between md and
   * lg, so the avatar stays on screen.
   */
  dense?: boolean
}

const Links = ({ dense = false }: LinksProps) => {
  const theme = useTheme()
  const location = useLocation()
  const { isRestrictedUser } = useRestrictedUserPermissions('Marketplace')

  const menuItems = useMemo(
    () =>
      LinksData.filter(link => {
        switch (link.label) {
          case 'Analytics':
            return !isRestrictedUser
          case 'Marketplace':
            return true // Always show Marketplace, even for restricted users
          default:
            return true
        }
      }),
    [isRestrictedUser]
  )

  return (
    <Box
      display="flex"
      justifyContent="center"
      data-testid={NavigationTestIds.navigationContainer}
      sx={{
        gap: 0,
        flexGrow: 1,
      }}
    >
      {menuItems.map(link => (
        <StyledLink
          key={link.label}
          to={link.route}
          data-testid={link['data-testid']}
        >
          <StyledButton
            startIcon={
              <link.icon
                filled={
                  Array.isArray(link.routesBase) &&
                  link.routesBase.some(subPath =>
                    location.pathname.startsWith(subPath)
                  )
                }
              />
            }
            className={
              Array.isArray(link.routesBase) &&
              link.routesBase.some(subPath =>
                location.pathname.startsWith(subPath)
              )
                ? 'active'
                : ''
            }
            sx={
              dense ? { [theme.breakpoints.only('md')]: { px: 1 } } : undefined
            }
          >
            <Typography
              variant="subtitle1"
              sx={[
                {
                  width: { xs: '100%', md: 'auto' },
                  display: { xs: 'flex', md: 'block' },
                  justifyContent: 'center',
                  ...(LinksData.length > 3 && {
                    fontSize: {
                      xs: '0.6rem',
                      sm: theme.typography.subtitle1.fontSize,
                    },
                  }),
                },
              ]}
            >
              {link.label}
            </Typography>
          </StyledButton>
        </StyledLink>
      ))}
    </Box>
  )
}
export default Links
